// ═══════════════════════════════════════════════════════════════
// DEBRIS — Contractors, Subcontractors & Vendor Performance
// Tampa prime/sub hierarchy with live spend from invoices.
// TRMETA blob now only stores: primeContractorId, mobilizationLevel,
// isMonitoringFirm. All other vendor metadata is in real table fields.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Checkbox,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconChevronDown, IconChevronRight, IconUser, IconPhone, IconMail,
  IconDownload, IconUpload,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { exportCSV } from '../../lib/exportCSV';
import { useDebrisStore } from '../../store/debris-store';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatPhone } from '../../lib/phoneFormat';
import { formatCurrency } from '../../lib/formatUtils';
import { parseContractorSpreadsheet, generateContractorTemplate, downloadWorkbook } from '../../lib/bulkImport';
import type { ContractorRow } from '../../lib/bulkImport';

// ── TRMETA blob — hierarchy-only fields ──────────────────────────
// managingDepartment, cityPOC, cityPOCEmail, contractCeiling,
// vendorCategory, qualityScore are now real table fields on DebrisContractor.

const META_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;

interface ContractorMeta {
  primeContractorId?: string;
  mobilizationLevel?: string;
  isMonitoringFirm?: string;
}

function decodeNotesMeta(raw: string): { notes: string; meta: ContractorMeta } {
  if (!raw) return { notes: '', meta: {} };
  const m = raw.match(META_RE);
  if (!m) return { notes: raw, meta: {} };
  try {
    const meta = JSON.parse(m[1]) as ContractorMeta;
    const notes = raw.replace(META_RE, '').trim();
    return { notes, meta };
  } catch { return { notes: raw, meta: {} }; }
}

function encodeNotesMeta(userNotes: string, meta: ContractorMeta): string {
  const clean = userNotes.replace(META_RE, '').trim();
  const nonEmpty = Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== '' && v !== undefined));
  if (Object.keys(nonEmpty).length === 0) return clean;
  const metaStr = JSON.stringify(nonEmpty);
  return clean ? `${clean}\n|||TRMETA|||${metaStr}|||ENDTRMETA|||` : `|||TRMETA|||${metaStr}|||ENDTRMETA|||`;
}

// ── Constants ─────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'completed', label: 'Completed' },
  { value: 'inactive', label: 'Inactive' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)', suspended: 'var(--color-error)',
  completed: 'var(--color-primary-700)', inactive: '#939598',
};

const DEPT_OPTIONS = [
  { value: 'Solid Waste', label: 'Solid Waste' },
  { value: 'Parks & Rec', label: 'Parks & Rec' },
  { value: 'Stormwater', label: 'Stormwater' },
  { value: 'Contracted', label: 'Contracted' },
];

const CATEGORY_OPTIONS = [
  { value: 'Debris Removal', label: 'Debris Removal' },
  { value: 'Tree Service', label: 'Tree Service' },
  { value: 'Equipment Rental', label: 'Equipment Rental' },
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Fencing', label: 'Fencing' },
  { value: 'Hauling', label: 'Hauling' },
  { value: 'Aggregates', label: 'Aggregates' },
  { value: 'Monitoring', label: 'Monitoring' },
  { value: 'Other', label: 'Other' },
];

const MOB_LEVEL_OPTIONS = [
  { value: '1', label: 'Level 1' },
  { value: '2', label: 'Level 2' },
  { value: '3', label: 'Level 3' },
];

const MOB_LEVEL_COLORS: Record<string, string> = {
  '1': '#16a34a', '2': '#d97706', '3': '#dc2626',
};

function fmtCurrencyDash(val: string | number | undefined): string {
  const n = typeof val === 'number' ? val : parseFloat(val as string || '');
  if (!isFinite(n) || n === 0) return '—';
  return formatCurrency(n);
}
function fmtInputNum(v: string | number | undefined) {
  if (v === undefined || v === '' || v === null) return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
const stripNumFmt = (s: string) => s.replace(/[^0-9.]/g, '');

// ── Component ─────────────────────────────────────────────────────

export default function Contractors() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { contractors, loadingContractors, loadContractors, createContractor, updateContractor, deleteContractor } = useDebrisStore();
  const { invoices, loadInvoices } = useDebrisOpsStore();

  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState<string | null>(null);
  const [expandedPrimes, setExpandedPrimes] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<typeof contractors[0] | null>(null);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<typeof contractors[0] | null>(null);
  const [primeForSubAdd, setPrimeForSubAdd] = useState<number | null>(null);

  // Bulk import state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<ContractorRow[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUpdateExisting, setBulkUpdateExisting] = useState(false);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const handleBulkFile = async (file: File) => {
    const { rows, errors } = await parseContractorSpreadsheet(file, contractors);
    setBulkRows(rows);
    setBulkErrors(errors.map(e => `Row ${e.row}: ${e.message}`));
    setBulkOpen(true);
    setBulkFile(file);
  };

  // Compute actionable rows for bulk import (respects update toggle)
  const actionableRows = useMemo(() => bulkRows.filter(r => {
    if (!r.valid && !r.existingId) return false; // truly invalid
    if (r.existingId && !bulkUpdateExisting) return false; // skip existing when toggle off
    if (r.errors.length > 0 && !r.existingId) return false;
    return !!r.companyName; // must have name
  }), [bulkRows, bulkUpdateExisting]);

  const bulkCreateCount = actionableRows.filter(r => !r.existingId).length;
  const bulkUpdateCount = actionableRows.filter(r => !!r.existingId).length;

  const handleBulkImport = async () => {
    setBulkImporting(true);
    let created = 0;
    let updated = 0;
    for (const row of actionableRows) {
      const payload: Record<string, unknown> = {
        incidentId,
        companyName: row.companyName,
        contractNumber: row.contractNumber,
        contactName: row.contactName,
        contactPhone: row.contactPhone,
        contactEmail: row.contactEmail,
        contractAmount: row.contractAmount,
        contractCeiling: row.contractCeiling,
        vendorCategory: row.vendorCategory,
        managingDepartment: row.managingDepartment,
        cityPOC: row.cityPOC,
        cityPOCEmail: row.cityPOCEmail,
        status: row.status || 'active',
      };
      // Handle prime/sub via TRMETA in notes
      if (row.primeContractorName) {
        const prime = contractors.find(c => c.companyName.toLowerCase().includes(row.primeContractorName.toLowerCase()));
        if (prime) {
          payload.notes = encodeNotesMeta(row.notes, { primeContractorId: String(prime.id) });
        } else {
          payload.notes = row.notes;
        }
      } else {
        payload.notes = row.notes;
      }
      if (row.existingId && bulkUpdateExisting) {
        await updateContractor(parseInt(row.existingId), payload);
        updated++;
      } else if (!row.existingId) {
        const id = await createContractor(payload);
        if (id) created++;
      }
    }
    const parts: string[] = [];
    if (created > 0) parts.push(`${created} created`);
    if (updated > 0) parts.push(`${updated} updated`);
    notifications.show({ message: `Contractors: ${parts.join(', ')}`, color: 'var(--color-success)' });
    setBulkImporting(false);
    setBulkOpen(false);
    setBulkRows([]);
    setBulkUpdateExisting(false);
    loadContractors();
  };

  const [form, setForm] = useState({
    companyName: '', contractNumber: '', contactName: '', contactPhone: '', contactEmail: '',
    contractAmount: '', status: 'active' as string, notes: '',
    // Real table fields
    vendorCategory: '', managingDepartment: '', cityPOC: '', cityPOCEmail: '',
    contractCeiling: '', qualityScore: '',
    // TRMETA-only hierarchy fields
    mobilizationLevel: '', isSub: false, primeContractorId: '',
  });

  useEffect(() => { loadContractors(); loadInvoices(); }, [loadContractors, loadInvoices]);

  // ── Live spend + substantiation from invoices ───────────────────
  const spendByContractor = useMemo(() => {
    const map: Record<string, { total: number; count: number; substantiated: number }> = {};
    invoices.forEach(inv => {
      if (inv.status === 'sub_invoice') return;
      const cid = String(inv.contractorId || '');
      if (!cid || cid === '0') return;
      if (!map[cid]) map[cid] = { total: 0, count: 0, substantiated: 0 };
      map[cid].total += parseFloat(inv.totalAmount || '0') || 0;
      map[cid].count += 1;
      try {
        const items: { amount?: unknown }[] = JSON.parse(inv.lineItems || '[]');
        map[cid].substantiated += items.reduce((s, i) => s + (parseFloat(String(i.amount || '0')) || 0), 0);
      } catch { /* ignore */ }
    });
    return map;
  }, [invoices]);

  const getSpend = (c: typeof contractors[0]) =>
    spendByContractor[String(c.id)]?.total ?? 0;
  const getCount = (c: typeof contractors[0]) =>
    spendByContractor[String(c.id)]?.count ?? 0;
  const getSubstantiation = (c: typeof contractors[0]) => {
    const d = spendByContractor[String(c.id)];
    if (!d || d.total === 0) return null;
    return Math.round((d.substantiated / d.total) * 100);
  };

  // ── Parse TRMETA for hierarchy-only fields ──────────────────────
  const contractorsWithMeta = useMemo(() =>
    contractors.map(c => {
      const { notes, meta } = decodeNotesMeta(c.notes);
      return { ...c, cleanNotes: notes, meta };
    }),
    [contractors],
  );

  // ── Filtered / sorted primes ─────────────────────────────────────
  const primes = useMemo(() => {
    let list = contractorsWithMeta.filter(c => !c.meta.primeContractorId);
    if (search) {
      const q = search.toLowerCase();
      const subMap = new Map<string, typeof contractorsWithMeta>();
      contractorsWithMeta.forEach(c => {
        if (c.meta.primeContractorId) {
          const arr = subMap.get(c.meta.primeContractorId) || [];
          arr.push(c);
          subMap.set(c.meta.primeContractorId, arr);
        }
      });
      list = list.filter(p => {
        const primeMatch = (p.companyName || '').toLowerCase().includes(q) || (p.contractNumber || '').toLowerCase().includes(q);
        const subs = subMap.get(String(p.id)) || [];
        const subMatch = subs.some(s => (s.companyName || '').toLowerCase().includes(q) || (s.contactName || '').toLowerCase().includes(q));
        return primeMatch || subMatch;
      });
    }
    if (deptFilter) list = list.filter(c => c.managingDepartment === deptFilter);
    if (catFilter) list = list.filter(c => c.vendorCategory === catFilter);
    return list.sort((a, b) => getSpend(b) - getSpend(a)); // default: spend desc
  }, [contractorsWithMeta, search, deptFilter, catFilter, spendByContractor]); // eslint-disable-line react-hooks/exhaustive-deps



  const getSubsForPrime = (primeId: number) =>
    contractorsWithMeta
      .filter(c => c.meta.primeContractorId === String(primeId))
      .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));

  const primeOptions = useMemo(() =>
    contractorsWithMeta
      .filter(c => !c.meta.primeContractorId)
      .map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
    [contractorsWithMeta],
  );

  const toggleExpanded = (id: number) => {
    setExpandedPrimes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Modal helpers ────────────────────────────────────────────────

  const openCreate = (primeId?: number) => {
    setEditTarget(null);
    setPrimeForSubAdd(primeId ?? null);
    setForm({
      companyName: '', contractNumber: '', contactName: '', contactPhone: '', contactEmail: '',
      contractAmount: '', status: 'active', notes: '',
      vendorCategory: '', managingDepartment: '', cityPOC: '', cityPOCEmail: '',
      contractCeiling: '', qualityScore: '',
      mobilizationLevel: '', isSub: !!primeId, primeContractorId: primeId ? String(primeId) : '',
    });
    setModalOpen(true);
  };

  const openEdit = (c: typeof contractorsWithMeta[0]) => {
    setEditTarget(c);
    setPrimeForSubAdd(null);
    setForm({
      companyName: c.companyName, contractNumber: c.contractNumber,
      contactName: c.contactName, contactPhone: c.contactPhone, contactEmail: c.contactEmail,
      contractAmount: c.contractAmount || '', status: c.status, notes: c.cleanNotes,
      // Real fields
      vendorCategory: c.vendorCategory || '',
      managingDepartment: c.managingDepartment || '',
      cityPOC: c.cityPOC || '',
      cityPOCEmail: c.cityPOCEmail || '',
      contractCeiling: c.contractCeiling || '',
      qualityScore: c.qualityScore || '',
      // TRMETA hierarchy
      mobilizationLevel: c.meta.mobilizationLevel || '',
      isSub: !!c.meta.primeContractorId,
      primeContractorId: c.meta.primeContractorId || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.companyName.trim()) {
      notifications.show({ message: 'Company name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const meta: ContractorMeta = {
        mobilizationLevel: form.mobilizationLevel,
        ...(form.isSub && form.primeContractorId ? { primeContractorId: form.primeContractorId } : {}),
      };
      const encodedNotes = encodeNotesMeta(form.notes, meta);

      const payload: Record<string, unknown> = {
        incidentId,
        companyName: form.companyName,
        contactName: form.contactName,
        contactPhone: form.contactPhone,
        contactEmail: form.contactEmail,
        contractNumber: form.contractNumber,
        contractAmount: form.contractAmount,
        status: form.status,
        notes: encodedNotes,
        // Real fields
        vendorCategory: form.vendorCategory,
        managingDepartment: form.managingDepartment,
        cityPOC: form.cityPOC,
        cityPOCEmail: form.cityPOCEmail,
        contractCeiling: form.contractCeiling,
        qualityScore: form.qualityScore,
      };

      if (editTarget) {
        await updateContractor(editTarget.id, payload);
        notifications.show({ message: 'Contractor updated', color: 'var(--color-success)' });
      } else {
        await createContractor(payload);
        notifications.show({ message: 'Contractor added', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteContractor(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.companyName} removed`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const setField = (key: string, value: string | boolean) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Stack gap={12}>
      {/* Header */}
      <Group justify="space-between">
        <Title order={3} c="black">Contractors &amp; Vendors</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingContractors} onClick={() => { loadContractors(); loadInvoices(); }}>Refresh</Button>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={() => {
              const headers = ['Company', 'Category', 'Department', 'Status', 'Total Spend', 'Invoices', 'Quality', 'Contract #', 'Ceiling', 'Contact', 'Email', 'Phone', 'City POC'];
              const rows = contractorsWithMeta.map(c => [
                c.companyName, c.vendorCategory || '', c.managingDepartment || '', c.status,
                String(getSpend(c)), String(getCount(c)), c.qualityScore || '',
                c.contractNumber, c.contractCeiling || '', c.contactName, c.contactEmail, c.contactPhone, c.cityPOC || '',
              ]);
              exportCSV('contractors.csv', headers, rows);
            }}>Export CSV</Button>
          {canEdit && (
            <>
              <Button size="xs" variant="light" leftSection={<IconUpload size={14} />}
                onClick={() => bulkFileRef.current?.click()}>Bulk Import</Button>
              <Button size="xs" variant="subtle" leftSection={<IconDownload size={14} />}
                onClick={() => downloadWorkbook(generateContractorTemplate(), 'contractor-import-template.xlsx')}>
                Download Template
              </Button>
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => openCreate()}>Add Contractor</Button>
            </>
          )}
          <input
            ref={bulkFileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleBulkFile(f);
              e.target.value = '';
            }}
          />
        </Group>
      </Group>

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search contractors or subs..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          style={{ flex: 1, minWidth: 200, maxWidth: 320 }}
        />
        <Select size="xs" placeholder="All Departments"
          data={[{ value: '', label: 'All Departments' }, ...DEPT_OPTIONS]}
          value={deptFilter || ''} onChange={v => setDeptFilter(v || null)} style={{ width: 160 }} clearable />
        <Select size="xs" placeholder="All Categories"
          data={[{ value: '', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
          value={catFilter || ''} onChange={v => setCatFilter(v || null)} style={{ width: 160 }} clearable />
        <Text size="xs" c="dimmed">
          {primes.length} prime{primes.length !== 1 ? 's' : ''} · {contractorsWithMeta.filter(c => !!c.meta.primeContractorId).length} subs
        </Text>
      </Group>

      {/* Prime Contractor Cards */}
      <div style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto', paddingRight: 4 }}>
        <Stack gap="sm">
          {loadingContractors && primes.length === 0 && (
            <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
              <Text c="dimmed">Loading contractors...</Text>
            </Paper>
          )}
          {!loadingContractors && primes.length === 0 && (
            <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
              <Text c="dimmed">No contractors found.</Text>
            </Paper>
          )}

          {primes.map(prime => {
            const subs = getSubsForPrime(prime.id);
            const isExpanded = expandedPrimes.has(prime.id);
            const ceiling = fmtCurrencyDash(prime.contractCeiling);
            const mobLevel = prime.meta.mobilizationLevel;
            const spend = getSpend(prime);
            const invoiceCount = getCount(prime);
            const substantiationPct = getSubstantiation(prime);
            const quality = prime.qualityScore && parseFloat(prime.qualityScore) > 0 ? parseFloat(prime.qualityScore) : null;

            return (
              <Paper key={prime.id} withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
                {/* Card Header */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', cursor: 'pointer',
                    background: isExpanded ? '#f8fafc' : '#fff',
                    borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                  }}
                  onClick={() => toggleExpanded(prime.id)}
                >
                  {isExpanded
                    ? <IconChevronDown size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                    : <IconChevronRight size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text fw={700} size="sm">{prime.companyName || '(unnamed)'}</Text>
                      {prime.contractNumber && <Text size="xs" c="dimmed">#{prime.contractNumber}</Text>}
                      <Badge size="sm" color={STATUS_COLORS[prime.status] ?? '#939598'}>{prime.status}</Badge>
                      {ceiling !== '—' && <Badge size="sm" variant="light" color="var(--color-primary-700)">{ceiling} ceiling</Badge>}
                      {mobLevel && <Badge size="sm" variant="light" color={MOB_LEVEL_COLORS[mobLevel] || '#64748b'}>CLAMP {mobLevel}</Badge>}
                      {prime.vendorCategory && <Badge size="sm" variant="light" color="blue">{prime.vendorCategory}</Badge>}
                      {quality !== null && (
                        <Badge size="sm" color={quality >= 90 ? 'green' : quality >= 70 ? 'orange' : 'red'}>
                          {quality.toFixed(0)}% quality
                        </Badge>
                      )}
                    </div>
                    {/* Spend + substantiation bar */}
                    {(spend > 0 || invoiceCount > 0) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <Text size="xs" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 72, color: '#1e293b' }}>
                          {formatCurrency(spend)}
                        </Text>
                        {substantiationPct !== null && (() => {
                          const barColor = substantiationPct > 105 ? '#7C3AED' : substantiationPct >= 95 ? 'var(--color-success)' : substantiationPct >= 85 ? '#C68700' : 'var(--color-error)';
                          return (
                            <>
                              <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 3, maxWidth: 180 }}>
                                <div style={{
                                  width: `${Math.min(substantiationPct, 100)}%`, height: '100%', borderRadius: 3, transition: 'width 0.3s',
                                  background: barColor,
                                }} />
                              </div>
                              <Text size="xs" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: barColor }}>
                                {substantiationPct}%
                              </Text>
                            </>
                          );
                        })()}
                        {invoiceCount > 0 && (
                          <Text size="xs" c="dimmed">{invoiceCount} invoice{invoiceCount !== 1 ? 's' : ''}</Text>
                        )}
                      </div>
                    )}
                    {/* Invoice breakdown */}
                    {invoiceCount > 0 && (() => {
                      const contractorInvs = invoices.filter(inv => String(inv.contractorId) === String(prime.id) && inv.status !== 'sub_invoice');
                      return (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                          {contractorInvs.map(inv => {
                            const amt = parseFloat(inv.totalAmount || '0') || 0;
                            let subst = 0;
                            try { const items: any[] = JSON.parse(inv.lineItems || '[]'); subst = items.reduce((s, i) => s + (parseFloat(String(i.amount || '0')) || 0), 0); } catch {}
                            const pct = amt > 0 ? Math.round((subst / amt) * 100) : 0;
                            const pctColor = pct > 105 ? '#7C3AED' : pct >= 95 ? 'var(--color-success)' : pct >= 85 ? '#C68700' : 'var(--color-error)';
                            const fmtDt = (d: string) => { if (!d) return ''; try { const p = d.slice(0, 10).split('-'); return `${p[1]}/${p[2]}/${p[0]}`; } catch { return d.slice(0, 10); } };
                            const period = inv.periodStart ? `${fmtDt(inv.periodStart)}${inv.periodEnd && inv.periodEnd !== inv.periodStart ? ' — ' + fmtDt(inv.periodEnd) : ''}` : '';
                            return (
                              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                                <span style={{ color: '#4B3FFF', fontWeight: 600, minWidth: 70 }}>#{inv.invoiceNumber}</span>
                                <span style={{ color: '#64748b', minWidth: 140 }}>{period || '—'}</span>
                                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 85, textAlign: 'right' }}>{formatCurrency(amt)}</span>
                                <span style={{ fontWeight: 700, color: pctColor, fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right' }}>{pct}%</span>
                                <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, background: inv.status === 'reconciled' ? '#dcfce7' : inv.status === 'approved' ? '#dbeafe' : '#f1f5f9', color: inv.status === 'reconciled' ? '#166534' : inv.status === 'approved' ? '#1e40af' : '#64748b' }}>
                                  {inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : 'Draft'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {subs.length} sub{subs.length !== 1 ? 's' : ''}
                  </Text>
                  <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <Group gap={4}>
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(prime)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(prime)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{ padding: '12px 16px 16px' }}>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13, marginBottom: 16 }}>
                      {prime.contactName && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}>
                          <IconUser size={13} style={{ color: '#9ca3af' }} /> {prime.contactName}
                        </span>
                      )}
                      {prime.contactPhone && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}>
                          <IconPhone size={13} style={{ color: '#9ca3af' }} /> {formatPhone(prime.contactPhone)}
                        </span>
                      )}
                      {prime.contactEmail && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}>
                          <IconMail size={13} style={{ color: '#9ca3af' }} /> {prime.contactEmail}
                        </span>
                      )}
                      {prime.managingDepartment && (
                        <span style={{ color: '#6b7280' }}>Dept: <strong>{prime.managingDepartment}</strong></span>
                      )}
                      {prime.cityPOC && (
                        <span style={{ color: '#6b7280' }}>
                          City POC: <strong>{prime.cityPOC}</strong>
                          {prime.cityPOCEmail && <span> ({prime.cityPOCEmail})</span>}
                        </span>
                      )}
                    </div>

                    {/* Subcontractors */}
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="xs" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Subcontractors ({subs.length})
                        </Text>
                        {canEdit && (
                          <Button size="xs" variant="light" leftSection={<IconPlus size={12} />}
                            onClick={() => openCreate(prime.id)}>
                            Add Sub
                          </Button>
                        )}
                      </Group>
                      {subs.length === 0 && (
                        <Text size="xs" c="dimmed" style={{ padding: '8px 0' }}>No subcontractors registered.</Text>
                      )}
                      {subs.length > 0 && (
                        <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                                <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Company</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Contact</th>
                                <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Status</th>
                                {canEdit && <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {subs.map(sub => (
                                <tr key={sub.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '6px 12px' }}>
                                    <Text size="sm" fw={500}>{sub.companyName || '—'}</Text>
                                  </td>
                                  <td style={{ padding: '6px 12px' }}>
                                    <Text size="xs">{sub.contactName || '—'}</Text>
                                    {sub.contactPhone && <Text size="xs" c="dimmed">{formatPhone(sub.contactPhone)}</Text>}
                                  </td>
                                  <td style={{ padding: '6px 12px' }}>
                                    <Badge size="xs" color={STATUS_COLORS[sub.status] ?? '#939598'}>{sub.status}</Badge>
                                  </td>
                                  {canEdit && (
                                    <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                                      <Group gap={4} justify="flex-end">
                                        <ActionIcon size="xs" variant="subtle" onClick={() => openEdit(sub)}>
                                          <IconPencil size={12} />
                                        </ActionIcon>
                                        <ActionIcon size="xs" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(sub)}>
                                          <IconTrash size={12} />
                                        </ActionIcon>
                                      </Group>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Paper>
            );
          })}
        </Stack>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editTarget
            ? (form.isSub ? 'Edit Subcontractor' : 'Edit Contractor')
            : (primeForSubAdd ? 'Add Subcontractor' : 'Add Contractor')
        }
        centered size="lg"
      >
        <Stack gap="md">
          <TextInput label="Company Name" required value={form.companyName}
            onChange={e => setField('companyName', e.target.value)} />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Contract Number" value={form.contractNumber}
              onChange={e => setField('contractNumber', e.target.value)} />
            <TextInput label="Contract Ceiling" value={fmtInputNum(form.contractCeiling)}
              onChange={e => setField('contractCeiling', stripNumFmt(e.target.value))} placeholder="e.g. 50,000,000"
              leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Category" data={CATEGORY_OPTIONS} value={form.vendorCategory || null}
              onChange={v => setField('vendorCategory', v ?? '')} clearable />
            <Select label="Department" data={DEPT_OPTIONS} value={form.managingDepartment || null}
              onChange={v => setField('managingDepartment', v ?? '')} clearable />
            <Select label="Status" data={STATUS_OPTIONS} value={form.status}
              onChange={v => setField('status', v ?? 'active')} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Contact Name" value={form.contactName}
              onChange={e => setField('contactName', e.target.value)} />
            <TextInput label="Phone" value={form.contactPhone}
              onChange={e => setField('contactPhone', e.target.value)} />
          </SimpleGrid>
          <TextInput label="Email" value={form.contactEmail}
            onChange={e => setField('contactEmail', e.target.value)} />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="City Point of Contact" value={form.cityPOC}
              onChange={e => setField('cityPOC', e.target.value)} />
            <TextInput label="City POC Email" value={form.cityPOCEmail}
              onChange={e => setField('cityPOCEmail', e.target.value)} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Mobilization Level" data={MOB_LEVEL_OPTIONS} value={form.mobilizationLevel || null}
              onChange={v => setField('mobilizationLevel', v ?? '')} clearable />
            <TextInput label="Quality Score (0–100)" type="number" value={form.qualityScore}
              onChange={e => setField('qualityScore', e.target.value)} min="0" max="100" />
          </SimpleGrid>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Group gap="md">
              <Checkbox label="Subcontractor" checked={form.isSub}
                onChange={e => setField('isSub', e.target.checked)} />
            </Group>
            {form.isSub && (
              <Select label="Prime Contractor" data={primeOptions} value={form.primeContractorId || null}
                onChange={v => setField('primeContractorId', v ?? '')} mt="sm"
                placeholder="Select prime contractor..." />
            )}
          </div>

          <Textarea label="Notes" value={form.notes} onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : (primeForSubAdd ? 'Add Subcontractor' : 'Add Contractor')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Contractor" centered size="sm">
        <Stack>
          <Text size="sm">Remove <strong>{deleteTarget?.companyName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal opened={bulkOpen} onClose={() => { setBulkOpen(false); setBulkRows([]); setBulkErrors([]); setBulkFile(null); setBulkUpdateExisting(false); }}
        title="Bulk Import Contractors" centered size="xl">
        <Stack gap="md">
          {bulkFile && (
            <Group gap="sm">
              <Text size="sm" fw={600}>{bulkFile.name}</Text>
              <Text size="sm" c="dimmed">{bulkRows.length} row{bulkRows.length !== 1 ? 's' : ''} parsed</Text>
            </Group>
          )}

          {bulkErrors.length > 0 && (
            <Paper withBorder p="sm" style={{ background: '#fef2f2', borderColor: '#fecaca', maxHeight: 120, overflowY: 'auto' }}>
              <Text size="xs" fw={700} c="var(--color-error)" mb={4}>
                {bulkErrors.length} error{bulkErrors.length !== 1 ? 's' : ''} found
              </Text>
              {bulkErrors.map((err, i) => (
                <Text key={i} size="xs" c="var(--color-error)">{err}</Text>
              ))}
            </Paper>
          )}

          {bulkRows.some(r => r.existingId) && (
            <Checkbox
              label="Update existing contractors with new data"
              checked={bulkUpdateExisting}
              onChange={e => setBulkUpdateExisting(e.target.checked)}
            />
          )}

          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Row</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Company Name</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Contract #</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Contact</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Status</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {bulkRows.map(row => {
                  const isUpdate = !!row.existingId && bulkUpdateExisting;
                  const isSkipped = !!row.existingId && !bulkUpdateExisting;
                  const hasErrors = row.errors.length > 0 && !row.existingId;
                  return (
                    <tr key={row.rowNum} style={{
                      borderBottom: '1px solid #f1f5f9',
                      background: hasErrors ? '#fef2f2' : isSkipped ? '#fffbeb' : isUpdate ? '#eff6ff' : undefined,
                      opacity: isSkipped ? 0.6 : undefined,
                    }}>
                      <td style={{ padding: '6px 10px', color: '#64748b' }}>{row.rowNum}</td>
                      <td style={{ padding: '6px 10px', fontWeight: 500 }}>{row.companyName || '(empty)'}</td>
                      <td style={{ padding: '6px 10px' }}>{row.contractNumber || '—'}</td>
                      <td style={{ padding: '6px 10px' }}>{row.contactName || '—'}</td>
                      <td style={{ padding: '6px 10px' }}>
                        <Badge size="xs" color={STATUS_COLORS[row.status] ?? '#939598'}>{row.status || '—'}</Badge>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {hasErrors
                          ? <Text size="xs" c="var(--color-error)">{row.errors.join('; ')}</Text>
                          : isUpdate
                            ? <Badge size="xs" color="#3b82f6">Update</Badge>
                            : isSkipped
                              ? <Badge size="xs" color="#d97706">Exists &mdash; skip</Badge>
                              : <Badge size="xs" color="var(--color-success)">Create</Badge>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {actionableRows.length} actionable of {bulkRows.length} rows
              {bulkCreateCount > 0 && ` (${bulkCreateCount} new`}
              {bulkCreateCount > 0 && bulkUpdateCount > 0 && `, ${bulkUpdateCount} update`}
              {bulkCreateCount > 0 && ')'}
              {bulkCreateCount === 0 && bulkUpdateCount > 0 && ` (${bulkUpdateCount} update)`}
              {bulkRows.some(r => r.existingId && !bulkUpdateExisting) &&
                ` · ${bulkRows.filter(r => r.existingId && !bulkUpdateExisting).length} skipped`}
            </Text>
            <Group gap="sm">
              <Button variant="default" size="xs" onClick={() => { setBulkOpen(false); setBulkRows([]); setBulkErrors([]); setBulkFile(null); setBulkUpdateExisting(false); }}>Cancel</Button>
              <Button size="xs" loading={bulkImporting}
                disabled={actionableRows.length === 0}
                onClick={handleBulkImport}>
                {bulkCreateCount > 0 && bulkUpdateCount > 0
                  ? `Import ${bulkCreateCount} new, update ${bulkUpdateCount} existing`
                  : bulkUpdateCount > 0
                    ? `Update ${bulkUpdateCount} contractor${bulkUpdateCount !== 1 ? 's' : ''}`
                    : `Import ${bulkCreateCount} contractor${bulkCreateCount !== 1 ? 's' : ''}`
                }
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
